import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import VideoRoom from '../components/visage/VideoRoom';
import { Video as VideoIcon, Clock } from 'lucide-react';

// Unauthenticated guest join: /visage/guest?t=<signed token>
// 409 = attorney not in the room yet -> waiting screen + auto-retry.
const RETRY_MS = 7000;

const GuestVisage = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('loading'); // loading | waiting | ready | inroom | error | left
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const timerRef = useRef(null);
  const [token, setToken] = useState(searchParams.get('t') || null);
  const slug = searchParams.get('s');

  const attempt = () => {
    fetch('/api/visage/guest/join', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (r.status === 409) {
          setState('waiting');
          timerRef.current = setTimeout(attempt, RETRY_MS);
          return;
        }
        if (!r.ok) throw new Error(data.detail || 'Unable to join this meeting.');
        setConfig({ ...data, _token: token });
        setState('ready');
      })
      .catch((e) => {
        setError(e.message);
        setState('error');
      });
  };

  // Strip any signed token from the address bar (browser history / referer hygiene).
  useEffect(() => {
    if (searchParams.get('t')) {
      window.history.replaceState({}, '', '/visage/guest');
    }
  }, [searchParams]);

  // A short link arrives as ?s=<slug>; exchange it for the token in a POST body (not a URL).
  useEffect(() => {
    if (token || !slug) return;
    fetch('/api/visage/guest/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug }),
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || 'This link is invalid or has expired.');
        setToken(d.token);
      })
      .catch((e) => { setError(e.message); setState('error'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) {
      if (!slug) {
        setError('This link is missing its access code. Please use the exact link from your email.');
        setState('error');
      }
      return;   // slug present but token not resolved yet -> the resolve effect will set it
    }
    attempt();
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-200 px-6">
      <div className="max-w-md text-center">{children}</div>
    </div>
  );

  if (state === 'loading') {
    return (
      <Shell>
        <VideoIcon className="w-10 h-10 mx-auto mb-4 animate-pulse text-cyan-400" />
        <p>Connecting you to your meeting…</p>
      </Shell>
    );
  }

  if (state === 'waiting') {
    return (
      <Shell>
        <Clock className="w-10 h-10 mx-auto mb-4 animate-pulse text-cyan-400" />
        <h1 className="text-xl font-semibold mb-3">You're a little early</h1>
        <p className="text-slate-400">
          Your attorney hasn't joined yet. Keep this page open — you'll be
          connected automatically as soon as the meeting starts.
        </p>
      </Shell>
    );
  }

  if (state === 'error') {
    return (
      <Shell>
        <h1 className="text-xl font-semibold mb-3">Unable to join</h1>
        <p className="text-slate-400">{error}</p>
        <p className="text-slate-500 mt-6 text-sm">
          If you believe this is a mistake, call Astraea Law at (904) 902-8214.
        </p>
      </Shell>
    );
  }

  if (state === 'left') {
    return (
      <Shell>
        <h1 className="text-xl font-semibold mb-3">You have left the meeting</h1>
        <p className="text-slate-400">You may close this window.</p>
      </Shell>
    );
  }

  if (state === 'ready') {
    return (
      <Shell>
        <VideoIcon className="w-10 h-10 mx-auto mb-4 text-cyan-400" />
        <h1 className="text-xl font-semibold mb-2">{config.room_name || 'Video meeting'}</h1>
        <p className="text-slate-400 mb-6">Joining as {config.display_name}</p>
        <button
          className="px-6 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
          onClick={() => setState('inroom')}
        >
          Join meeting
        </button>
        <p className="text-slate-500 mt-4 text-sm">
          Your browser will ask permission to use your camera and microphone.
        </p>
      </Shell>
    );
  }

  return (
    <VideoRoom
      room={{ id: config.room_id, name: config.room_name || 'Video meeting' }}
      guestConfig={config}
      onLeave={() => {
        fetch('/api/visage/guest/leave', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        }).catch(() => {});
        setState('left');
      }}
    />
  );
};

export default GuestVisage;
